import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { pbxApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PhoneCall, PhoneOff, PhoneIncoming } from 'lucide-react';
import { startRingtone, stopRingtone } from '../lib/ringtone';
import { enforceSecureRtpProfile, isSdpEncrypted, hasDtlsFingerprint } from '../lib/sdpSecurity';

export const CALL_STATUS = {
  IDLE: 'idle',
  DIALING: 'dialing',
  RINGING: 'ringing',
  INCOMING: 'incoming',
  CONNECTED: 'connected',
};

const PBXContext = createContext(null);

export function usePBX() {
  return useContext(PBXContext);
}

export function PBXProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const { getSocket, connected } = useWebSocket();

  const [exerciseId, setExerciseId] = useState(null);
  const [myPhone, setMyPhone] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [onlineExtensions, setOnlineExtensions] = useState([]);
  const [callStatus, setCallStatus] = useState(CALL_STATUS.IDLE);
  const [currentCall, setCurrentCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [micPermission, setMicPermission] = useState('unknown');

  // Stable refs for async callbacks and toast handlers
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const incomingCallRef = useRef(null);
  const myPhoneRef = useRef(null);
  const phoneNumbersRef = useRef([]);
  const callStatusRef = useRef(CALL_STATUS.IDLE);
  const currentCallRef = useRef(null);
  const callDurationRef = useRef(0);
  const exerciseIdRef = useRef(null);
  const registeredRef = useRef(false);
  // Per-call session ID to prevent crossed-wire signaling on concurrent calls
  const callSessionIdRef = useRef(null);
  // Queue ICE candidates that arrive before remote description is set
  const pendingIceCandidatesRef = useRef([]);
  const remoteDescriptionSetRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);
  useEffect(() => { myPhoneRef.current = myPhone; }, [myPhone]);
  useEffect(() => { phoneNumbersRef.current = phoneNumbers; }, [phoneNumbers]);
  useEffect(() => { callStatusRef.current = callStatus; }, [callStatus]);
  useEffect(() => { currentCallRef.current = currentCall; }, [currentCall]);
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);
  useEffect(() => { exerciseIdRef.current = exerciseId; }, [exerciseId]);

  // Check and request microphone permission proactively
  const checkMicPermission = useCallback(async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setMicPermission(result.state);
        result.onchange = () => setMicPermission(result.state);
      }
    } catch {
      setMicPermission('unknown');
    }
  }, []);

  const requestMicPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicPermission('granted');
      toast.success('Microphone access enabled');
      return true;
    } catch (err) {
      setMicPermission('denied');
      return false;
    }
  }, []);

  useEffect(() => {
    checkMicPermission();
  }, [checkMicPermission]);

  // Derive exerciseId from current URL
  useEffect(() => {
    const match = location.pathname.match(/\/(exercises|control-room|pbx-admin)\/([^/]+)/);
    const newId = match ? match[2] : null;

    if (newId) {
      setExerciseId(prev => (prev !== newId ? newId : prev));
    } else if ((location.pathname === '/phone' || location.pathname === '/dashboard') && user) {
      (async () => {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
          const token = localStorage.getItem('ems_token');
          const res = await fetch(`${API_URL}/exercises`, {
            headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
            credentials: 'omit',
          });
          if (res.ok) {
            const exercises = await res.json();
            const active = exercises.find(e => e.status === 'active') || exercises[0];
            if (active) {
              setExerciseId(prev => (prev !== active.exercise_id ? active.exercise_id : prev));
            } else {
              setExerciseId(null);
            }
          }
        } catch {
          // ignore
        }
      })();
    } else {
      setExerciseId(prev => (prev !== null ? null : prev));
    }
  }, [location.pathname, user]);

  // Load phone data + register PBX extension via Socket.IO
  useEffect(() => {
    if (!user || !exerciseId) {
      setMyPhone(null);
      setPhoneNumbers([]);
      setOnlineExtensions([]);
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        const [myPhoneData, numbersData, logsData, vmData] = await Promise.all([
          pbxApi.getMyPhone(exerciseId),
          pbxApi.listNumbers(exerciseId),
          pbxApi.listCalls(exerciseId, 20),
          pbxApi.listVoicemails(exerciseId),
        ]);

        if (!mounted) return;

        const phone = myPhoneData.assigned ? myPhoneData.number : null;
        setMyPhone(phone);
        setPhoneNumbers(numbersData);
        setCallLogs(logsData);
        setVoicemails(vmData);

        // Register PBX extension via Socket.IO
        if (phone && connected) {
          const socket = getSocket();
          if (socket) {
            socket.emit('pbx_register', {
              exercise_id: exerciseId,
              extension: phone.extension,
            });
            registeredRef.current = true;

            // Request online users
            socket.emit('pbx_get_online', { exercise_id: exerciseId });
          }
        }
      } catch (err) {
        console.error('PBX load error:', err);
      }
    };

    load();

    // Presence polling interval (10s) — still needed as a safety net
    const presenceInterval = setInterval(() => {
      const socket = getSocket();
      if (socket?.connected && exerciseId) {
        socket.emit('pbx_get_online', { exercise_id: exerciseId });
      }
    }, 10000);

    return () => {
      mounted = false;
      clearInterval(presenceInterval);

      // Unregister PBX extension
      if (registeredRef.current) {
        const socket = getSocket();
        const phone = myPhoneRef.current;
        if (socket && phone) {
          socket.emit('pbx_unregister', {
            exercise_id: exerciseId,
            extension: phone.extension,
          });
        }
        registeredRef.current = false;
      }
    };
  }, [exerciseId, user, connected, getSocket]);

  // Listen for PBX Socket.IO events
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleIncomingCall = (data) => {
      if (data.type === 'offer') {
        // V14 BUG FIX: Hard-reject 2nd concurrent call with busy signal
        if (callStatusRef.current !== CALL_STATUS.IDLE) {
          console.warn('[PBX] Rejecting incoming call — already on a call', { state: callStatusRef.current, from: data.from });
          const sock = getSocket();
          sock?.emit('pbx_busy', {
            exercise_id: exerciseIdRef.current,
            to_extension: data.from,
            session_id: data.session_id,
          });
          return;
        }
        handleIncomingSignal(data);
      }
    };

    const handleCallAnswer = (data) => {
      handleAnswerSignal(data);
    };

    const handleIceCandidate = (data) => {
      if (!data.candidate) return;
      // V14 BUG FIX: ignore ICE for stale call sessions
      if (data.session_id && callSessionIdRef.current && data.session_id !== callSessionIdRef.current) {
        console.warn('[PBX] Ignoring ICE for stale session', data.session_id);
        return;
      }
      const pc = peerConnectionRef.current;
      if (!pc) return;
      // V14 BUG FIX: queue ICE candidates until remote description set
      if (!remoteDescriptionSetRef.current) {
        pendingIceCandidatesRef.current.push(data.candidate);
        return;
      }
      pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((err) => {
        console.error('[PBX] addIceCandidate error:', err);
      });
    };

    const handleCallHangup = () => {
      endCall(false);
    };

    const handlePbxBusy = (data) => {
      console.warn('[PBX] Remote party busy', data);
      toast.error('Line busy — try again later');
      endCall(false);
    };

    const handlePbxError = (data) => {
      toast.error(data.message);
      setCallStatus(CALL_STATUS.IDLE);
    };

    const handleOnlineUsers = (data) => {
      setOnlineExtensions(data.extensions || []);
    };

    const handlePresenceUpdate = (data) => {
      if (data.type === 'user_online') {
        setOnlineExtensions(prev => [...new Set([...prev, data.extension])]);
      } else if (data.type === 'user_offline') {
        setOnlineExtensions(prev => prev.filter(e => e !== data.extension));
      }
    };

    socket.on('incoming_call', handleIncomingCall);
    socket.on('call_answer', handleCallAnswer);
    socket.on('ice_candidate', handleIceCandidate);
    socket.on('call_hangup', handleCallHangup);
    socket.on('pbx_busy', handlePbxBusy);
    socket.on('pbx_error', handlePbxError);
    socket.on('online_users', handleOnlineUsers);
    socket.on('presence_update', handlePresenceUpdate);

    // V14.4 — Live Workshop emergency controls
    const handleHangupAll = (data) => {
      console.warn('[PBX] Hangup-all signal received', data);
      if (callStatusRef.current !== CALL_STATUS.IDLE) {
        toast.warning(`Call ended by ${data?.by_name || 'controller'} (Hangup All)`);
        endCall(true);
      }
    };
    const handleMuteAll = (data) => {
      console.warn('[PBX] Mute-all signal received', data);
      const ls = localStreamRef.current;
      if (ls) {
        ls.getAudioTracks().forEach((t) => { t.enabled = false; });
        setIsMuted(true);
        toast.info(`Mic muted by ${data?.by_name || 'controller'} (Mute All)`);
      }
    };
    socket.on('pbx_hangup_all', handleHangupAll);
    socket.on('pbx_mute_all', handleMuteAll);

    return () => {
      socket.off('incoming_call', handleIncomingCall);
      socket.off('call_answer', handleCallAnswer);
      socket.off('ice_candidate', handleIceCandidate);
      socket.off('call_hangup', handleCallHangup);
      socket.off('pbx_busy', handlePbxBusy);
      socket.off('pbx_error', handlePbxError);
      socket.off('online_users', handleOnlineUsers);
      socket.off('presence_update', handlePresenceUpdate);
      socket.off('pbx_hangup_all', handleHangupAll);
      socket.off('pbx_mute_all', handleMuteAll);
    };
  }, [connected, getSocket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIncomingSignal = (data) => {
    const numbers = phoneNumbersRef.current;
    const callerNumber = numbers.find(n => n.extension === data.from);
    const callerRole = data.from_role || callerNumber?.role_name || 'Unknown';

    if (!data.sdp) {
      console.error('PBX: Received offer with missing SDP — ignoring');
      return;
    }

    // V14 BUG FIX: Track the session ID from the caller for the entire call
    callSessionIdRef.current = data.session_id || null;

    const ic = { extension: data.from, role: callerRole, sdp: data.sdp, session_id: data.session_id };
    incomingCallRef.current = ic;
    setIncomingCall(ic);
    setCallStatus(CALL_STATUS.INCOMING);
    startRingtone();

    toast.custom(
      () => (
        <div
          className="flex items-center gap-3 p-3 bg-background border border-border rounded-xl shadow-xl w-[320px]"
          data-testid="incoming-call-toast"
        >
          <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
            <PhoneIncoming className="w-4 h-4 text-green-600 dark:text-green-400 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-none">Incoming Call</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{callerRole} · Ext. {data.from}</p>
          </div>
          <Button
            size="sm"
            className="h-8 bg-green-500 hover:bg-green-600 text-white px-2 shrink-0"
            data-testid="toast-answer-call-btn"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); toast.dismiss('pbx-incoming'); answerCallRef.current?.(); }}
          >
            <PhoneCall className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-8 px-2 shrink-0"
            data-testid="toast-reject-call-btn"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); toast.dismiss('pbx-incoming'); rejectCallRef.current?.(); }}
          >
            <PhoneOff className="w-3.5 h-3.5" />
          </Button>
        </div>
      ),
      { id: 'pbx-incoming', duration: Infinity, position: 'bottom-right' }
    );
  };

  const answerCallRef = useRef(null);
  const rejectCallRef = useRef(null);
  const isAnsweringRef = useRef(false);

  const answerCall = useCallback(async () => {
    if (isAnsweringRef.current) return;
    isAnsweringRef.current = true;

    const ic = incomingCallRef.current;
    if (!ic || !ic.sdp) {
      console.error('PBX: answerCall called with missing incomingCall or SDP');
      toast.error('Call signal lost — please ask the caller to redial');
      setCallStatus(CALL_STATUS.IDLE);
      setIncomingCall(null);
      isAnsweringRef.current = false;
      return;
    }

    toast.dismiss('pbx-incoming');
    stopRingtone();
    setIncomingCall(null);

    try {
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      const pc = createPeerConnection(ic.extension);
      localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
      // V14 BUG FIX: only setRemoteDescription when in a state that accepts an offer
      if (pc.signalingState === 'stable' || pc.signalingState === 'have-remote-offer') {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: ic.sdp }));
      } else {
        console.warn('[PBX] Skipping setRemoteDescription(offer) — wrong state:', pc.signalingState);
      }
      remoteDescriptionSetRef.current = true;
      // Drain queued ICE candidates
      const queued = pendingIceCandidatesRef.current;
      pendingIceCandidatesRef.current = [];
      for (const cand of queued) {
        try { await pc.addIceCandidate(new RTCIceCandidate(cand)); } catch (e) { console.error('[PBX] queued ICE error', e); }
      }
      const answer = await pc.createAnswer();
      // V14 Bug 8: ensure SRTP/DTLS-SRTP on the answer
      const secureAnswerSdp = enforceSecureRtpProfile(answer.sdp);
      if (!isSdpEncrypted(secureAnswerSdp) || !hasDtlsFingerprint(secureAnswerSdp)) {
        console.error('[SRTP] Answer SDP is not encrypted — aborting');
        toast.error('Secure media negotiation failed');
        setCallStatus(CALL_STATUS.IDLE);
        return;
      }
      await pc.setLocalDescription({ type: 'answer', sdp: secureAnswerSdp });

      // Send answer via Socket.IO instead of raw WebSocket
      const socket = getSocket();
      socket?.emit('pbx_answer', {
        exercise_id: exerciseIdRef.current,
        to_extension: ic.extension,
        sdp: secureAnswerSdp,
        session_id: callSessionIdRef.current,
      });

      setCurrentCall({ extension: ic.extension, role: ic.role, direction: 'incoming' });
      setCallStatus(CALL_STATUS.CONNECTED);
      startCallTimer();
    } catch (err) {
      console.error('PBX answerCall error:', err);
      toast.error('Failed to answer — check microphone permission');
      setCallStatus(CALL_STATUS.IDLE);
    } finally {
      isAnsweringRef.current = false;
    }
  }, [getSocket]); // eslint-disable-line react-hooks/exhaustive-deps

  const rejectCall = useCallback(() => {
    const ic = incomingCallRef.current;
    toast.dismiss('pbx-incoming');
    stopRingtone();
    if (ic) {
      const socket = getSocket();
      socket?.emit('pbx_hangup', {
        exercise_id: exerciseIdRef.current,
        to_extension: ic.extension,
        session_id: callSessionIdRef.current,
      });
    }
    setIncomingCall(null);
    setCallStatus(CALL_STATUS.IDLE);
    callSessionIdRef.current = null;
    pendingIceCandidatesRef.current = [];
    remoteDescriptionSetRef.current = false;
  }, [getSocket]);

  answerCallRef.current = answerCall;
  rejectCallRef.current = rejectCall;

  const handleAnswerSignal = async (data) => {
    if (!peerConnectionRef.current) return;
    if (!data.sdp) {
      console.error('PBX: Received answer with missing SDP — ignoring');
      return;
    }
    // V14 BUG FIX: ignore answers for stale call sessions
    if (data.session_id && callSessionIdRef.current && data.session_id !== callSessionIdRef.current) {
      console.warn('[PBX] Ignoring answer for stale session', data.session_id);
      return;
    }
    const pc = peerConnectionRef.current;
    // V14 BUG FIX: only setRemoteDescription(answer) if we're awaiting one
    if (pc.signalingState !== 'have-local-offer') {
      console.warn('[PBX] Skipping setRemoteDescription(answer) — wrong state:', pc.signalingState);
      return;
    }
    try {
      await pc.setRemoteDescription(
        new RTCSessionDescription({ type: 'answer', sdp: data.sdp })
      );
      remoteDescriptionSetRef.current = true;
      // Drain queued ICE candidates
      const queued = pendingIceCandidatesRef.current;
      pendingIceCandidatesRef.current = [];
      for (const cand of queued) {
        try { await pc.addIceCandidate(new RTCIceCandidate(cand)); } catch (e) { console.error('[PBX] queued ICE error', e); }
      }
      setCallStatus(CALL_STATUS.CONNECTED);
      startCallTimer();
    } catch (err) {
      console.error('PBX handleAnswerSignal error:', err);
      toast.error('Call setup failed — please try again');
      endCall(false);
    }
  };

  const createPeerConnection = (targetExtension) => {
    const iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];

    const turnUrl = process.env.REACT_APP_TURN_URL;
    const turnUsername = process.env.REACT_APP_TURN_USERNAME;
    const turnPassword = process.env.REACT_APP_TURN_PASSWORD;

    if (turnUrl) {
      iceServers.push({
        urls: turnUrl,
        username: turnUsername || '',
        credential: turnPassword || '',
      });
      if (turnUrl.startsWith('turn:')) {
        iceServers.push({
          urls: turnUrl.replace('turn:', 'turns:'),
          username: turnUsername || '',
          credential: turnPassword || '',
        });
      }
    }

    const pc = new RTCPeerConnection({ iceServers });
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const socket = getSocket();
        socket?.emit('pbx_ice_candidate', {
          exercise_id: exerciseIdRef.current,
          to_extension: targetExtension,
          candidate: event.candidate,
          session_id: callSessionIdRef.current,
        });
      }
    };
    pc.ontrack = (event) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0];
      }
    };
    peerConnectionRef.current = pc;
    return pc;
  };

  const makeCall = async (targetExtension) => {
    const phone = myPhoneRef.current;
    const numbers = phoneNumbersRef.current;
    if (!phone || callStatusRef.current !== CALL_STATUS.IDLE) return;

    const target = numbers.find(n => n.extension === targetExtension);
    if (!target) { toast.error('Invalid extension'); return; }

    try {
      // V14 BUG FIX: generate a unique session ID for this call to prevent crossed signaling
      callSessionIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `call_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      remoteDescriptionSetRef.current = false;
      pendingIceCandidatesRef.current = [];

      setCallStatus(CALL_STATUS.DIALING);
      setCurrentCall({ extension: targetExtension, role: target.role_name, direction: 'outgoing' });
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      const pc = createPeerConnection(targetExtension);
      localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
      const offer = await pc.createOffer();
      // V14 Bug 8: ensure SRTP/DTLS-SRTP — all m= lines must use RTP/SAVPF
      const secureSdp = enforceSecureRtpProfile(offer.sdp);
      if (!isSdpEncrypted(secureSdp) || !hasDtlsFingerprint(secureSdp)) {
        console.error('[SRTP] Outbound offer SDP is not encrypted — refusing to send');
        toast.error('Secure media negotiation failed — call aborted');
        setCallStatus(CALL_STATUS.IDLE);
        setCurrentCall(null);
        return;
      }
      await pc.setLocalDescription({ type: 'offer', sdp: secureSdp });

      // Send offer via Socket.IO
      const socket = getSocket();
      socket?.emit('pbx_call', {
        exercise_id: exerciseIdRef.current,
        to_extension: targetExtension,
        sdp: secureSdp,
        from_role: phone.role_name,
        session_id: callSessionIdRef.current,
      });

      setCallStatus(CALL_STATUS.RINGING);
    } catch (err) {
      toast.error('Failed to start call — check microphone permission');
      setCallStatus(CALL_STATUS.IDLE);
      setCurrentCall(null);
    }
  };

  const endCall = useCallback(async (sendHangup = true) => {
    stopRingtone();
    const call = currentCallRef.current;
    const phone = myPhoneRef.current;
    const numbers = phoneNumbersRef.current;
    const duration = callDurationRef.current;
    const exId = exerciseIdRef.current;

    if (sendHangup && call) {
      const socket = getSocket();
      socket?.emit('pbx_hangup', {
        exercise_id: exId,
        to_extension: call.extension,
        session_id: callSessionIdRef.current,
      });
    }

    // Log the call
    if (call && phone && exId) {
      const targetNumber = numbers.find(n => n.extension === call.extension);
      try {
        await pbxApi.logCall({
          exercise_id: exId,
          from_extension: call.direction === 'outgoing' ? phone.extension : call.extension,
          to_extension: call.direction === 'outgoing' ? call.extension : phone.extension,
          from_role: call.direction === 'outgoing' ? phone.role_name : call.role,
          to_role: call.direction === 'outgoing' ? (targetNumber?.role_name || call.role) : phone.role_name,
          call_type: 'internal',
          duration_seconds: duration,
          status: duration > 0 ? 'completed' : 'missed',
        });
        const logsData = await pbxApi.listCalls(exId, 20);
        setCallLogs(logsData);
      } catch (err) {
        console.error('Failed to log call:', err);
      }
    }

    // Cleanup
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    setCallStatus(CALL_STATUS.IDLE);
    setCurrentCall(null);
    setCallDuration(0);
    setIsMuted(false);
    // V14 BUG FIX: reset session ID and ICE queue
    callSessionIdRef.current = null;
    pendingIceCandidatesRef.current = [];
    remoteDescriptionSetRef.current = false;
  }, [getSocket]);

  const startCallTimer = () => {
    setCallDuration(0);
    callDurationRef.current = 0;
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => {
        const next = prev + 1;
        callDurationRef.current = next;
        return next;
      });
    }, 1000);
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(prev => !prev);
    }
  };

  const refreshCallLogs = async () => {
    if (exerciseId) {
      try {
        const [logsData, vmData] = await Promise.all([
          pbxApi.listCalls(exerciseId, 20),
          pbxApi.listVoicemails(exerciseId),
        ]);
        setCallLogs(logsData);
        setVoicemails(vmData);
      } catch (err) {
        console.error('Failed to refresh logs:', err);
      }
    }
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const value = {
    exerciseId,
    myPhone,
    phoneNumbers,
    onlineExtensions,
    callStatus,
    currentCall,
    incomingCall,
    callDuration,
    isMuted,
    callLogs,
    voicemails,
    remoteAudioRef,
    makeCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    refreshCallLogs,
    formatDuration,
    micPermission,
    requestMicPermission,
  };

  return (
    <PBXContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay />
    </PBXContext.Provider>
  );
}
